import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import AuthModal from '../../components/AuthModal';
import { saveProfile } from '../../ducks/Profile/actions';
import { setIsLogin } from '../../ducks/Global/actions';
import { loginUser } from '../../ducks/Auth/actions';
import { ASSESSMENT_TYPES } from '../../config/assessmentConfigs';
import { useSubdomainBrand } from '../../components/SubdomainBrandProvider';
import { resolveViewMode } from '../../utils/viewMode';

/**
 * Login page wrapper — V2 design.
 *
 * Centers the AuthModal card on a full-viewport canvas with a footer
 * legal line. The actual form chrome (inputs, buttons, password
 * toggle, errors) lives inside AuthModal.
 */
const Login = () => {
  const auth = useSelector((state) => state.auth);
  const isLogin = useSelector((state) => state.global.isLogin);
  const dispatch = useDispatch();
  const history = useHistory();
  // On a white-label subdomain (trainercompany.getnubod.com) the colors
  // are already applied by ThemeProvider; here the card's logo slot shows
  // the coach's logo (or business name) instead of Nubod's, so the sign-in
  // page reads as theirs.
  const { brand: tenantBrand, found: isTenant } = useSubdomainBrand();
  // The logo URL comes from a public endpoint and is rendered to
  // anonymous visitors — only honor a plain https URL so a stray
  // scheme (data:, javascript:, an off-protocol beacon) can't be
  // injected into the pre-login page.
  const safeLogo =
    typeof tenantBrand?.logo === 'string' &&
    /^https:\/\//i.test(tenantBrand.logo.trim())
      ? tenantBrand.logo.trim()
      : null;
  const cardBrand =
    isTenant && tenantBrand
      ? { logo: safeLogo, name: tenantBrand.name || null }
      : null;

  useEffect(() => {
    dispatch(setIsLogin(true));
  }, [dispatch]);

  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      const role = auth.user?.role;
      const isTrainerPlus =
        role === 'trainer' ||
        role === 'organization_admin' ||
        role === 'platform_admin';
      const hasCompletedAssessment = auth.user?.isAssessmentCompleted;
      const assessmentType = auth.user?.assessmentType || ASSESSMENT_TYPES.FULL;
      const isAssessmentNone = assessmentType === ASSESSMENT_TYPES.NONE;

      let target;
      if (isTrainerPlus) {
        // Mirror AuthModal.resolveLoginLanding: "view as client" is gated on the
        // server canSelfTrack flag, so a user who can't self-track always lands
        // on the studio overview — even with a stale localStorage viewMode.
        let stored = 'trainer';
        try {
          stored = localStorage.getItem('viewMode') || 'trainer';
        } catch (_) {
          // ignore — localStorage can be blocked in some browsers
        }
        target =
          resolveViewMode(auth.user, stored) === 'client'
            ? '/dashboard/home'
            : '/dashboard/admin';
      } else if (hasCompletedAssessment) {
        target = '/dashboard/home';
      } else if (isAssessmentNone) {
        target = '/quick-setup';
      } else {
        target = '/assessment';
      }
      history.push(target);
    }
  }, [auth.isAuthenticated, auth.user, history]);

  return (
    <div className="login-page">
      <div className="login-page-card">
        <AuthModal
          brand={cardBrand}
          isAssessment={
            auth.user?.isAssessmentCompleted || auth.user?.role === 'trainer'
          }
          // Used by AuthModal's registration callback only (login goes
          // through resolveLoginLanding inside AuthModal). Trainers
          // land on their studio overview; assessment-complete clients
          // land on /dashboard/home; the rest go through assessment
          // or quick-setup depending on type.
          authRedirectRoute={
            auth.user?.role === 'trainer'
              ? '/dashboard/admin'
              : auth.user?.isAssessmentCompleted
              ? '/dashboard/home'
              : auth.user?.assessmentType === ASSESSMENT_TYPES.NONE
              ? '/quick-setup'
              : '/assessment'
          }
          isLogin={isLogin}
          saveProfile={(payload) => dispatch(saveProfile(payload))}
          loginUser={(formData, cb) => dispatch(loginUser(formData, cb))}
        />
      </div>
      <div className="login-page-footer muted">
        © {new Date().getFullYear()} Nubod · getnubod.com
      </div>
    </div>
  );
};

export default Login;
